<template>
  <div>
    <b-overlay :show="is_rendered">
      <div>
        <div
          :id="`chart-${chart_id}`"
          class="business-case-link cursor mb-2 fw-500 pt-4"
          @click="toggleChartFromAccordion"
        >

          <div v-if="!print_mode" class="float-right mb-2">
            <b-button variant="outline-dark" size="sm mr-2 pr-2" @click="download_as_png">
              <b-icon icon="file-earmark-image" scale="1" shift-v="0" aria-hidden="true" />
              PNG
            </b-button>
            <b-button variant="outline-dark" size="sm pr-2" @click="download_as_pdf">
              <b-icon icon="file-earmark-pdf" scale="1" shift-v="0" aria-hidden="true" />
              PDF
            </b-button>
          </div>
        </div>

        <div>
          <canvas
            :id="'chart_canvas_' + chart_id"
            ref="qesgChart"
            :style="'max-height: ' + chartData?.options?.chart_height + 'px;'"
            class="border border-light shadow-sm rounded mt-2"
          ></canvas>
          <small v-if="chartData && chartData.options" class="text-muted p-4"  style="white-space: pre-line">
            {{ chartData.options.footnote }}
          </small>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
  import Chart from 'chart.js/auto';
  import { mapState } from 'vuex';
  import html2canvas from 'html2canvas';
  import html2pdf from 'html2pdf.js';

  export default {
    name: 'ChartCanvasViewer',
    props: {
      payload: { type: Object, default: () => null },
      chart_id: { type: Number, default: () => null },
      print_mode: { type: Boolean, default: () => false },
    },
    computed: {
      ...mapState('report_list', ['lang']),
      ...mapState('charts', ['chart_data']),
    },
    data() {
      return {
        chart: null,
        chartData: null,
        chartData_e: null,
        is_rendered: true,
      };
    },
    watch: {
      lang: {
        immediate: true,
        handler() {
          this.renderChart();
        },
      },
    },
    beforeDestroy() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    mounted() {
      if (this.payload !== null) {
        this.chartData = { ...this.payload };
      }

      if (!this.print_mode) {
        this.$nextTick(() => {
          this.renderChart();
        });
      } 
    },
    methods: {
      toggleChartFromAccordion(event) {
        const targetElement = event.currentTarget.nextElementSibling;
        if (targetElement) {
          targetElement.classList.toggle('hide');
        }
      },
      async renderChart() {
        if (!this.$refs.qesgChart) {
          return;
        }
        let chartOptions = null;

        if (this.chartData === null) {
          let response = this.chart_data.find((obj) => obj['chart_id'] === this.chart_id);
          if (!response) {
            response = await axios.get(
              `${this.$store.state.backend_host}/getChartDataById?id=${this.chart_id}`
            );
            if (response.data.code) {
              this.chartData = response.data.result.contents;
              this.chartData_e = response.data.result.contents_em
                ? response.data.result.contents_em
                : response.data.result.contents_ea;
            }
          } else {
            this.chartData = response['form'];
            this.chartData_e = response['form_em'] ? response['form_em'] : response['form_ea'];
          }
        }

        if (this.lang !== 'ENG') {
          chartOptions = { ...this.chartData };
        } else {
          chartOptions = { ...this.chartData_e };
        }

        chartOptions.plugins = [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            },
          },
        ];

        chartOptions.options.plugins['tooltip'] = {
          intersect: true,
          callbacks: {
            label: (context) => {
              return context.parsed.y !== undefined
                ? `${context.dataset.label}: ${context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`
                : `${context.dataset.label}: ${context.parsed.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`;
            },
          },
        };
        if (this.chart) {
          this.chart.destroy();
        }
        const ctx = this.$refs.qesgChart.getContext('2d');
        this.chart = new Chart(ctx, chartOptions);
        this.is_rendered = false;
      },
      async download_as_png() {
        const chartElement = this.$refs.qesgChart;

        try {
          // html2canvas를 사용하여 요소 캡처
          const canvas = await html2canvas(chartElement);

          // canvas의 데이터 URL 생성
          const dataURL = canvas.toDataURL('image/png');

          // 데이터 URL을 사용하여 이미지 다운로드
          const link = document.createElement('a');
          link.href = dataURL;

          const file_name = this.lang !== 'ENG' ? this.chartData.label : this.chartData_e.label;
          link.download = file_name + '.png';
          link.click();
        } catch (error) {
          error.message = '[차트 다운로드]' + error.message;
          this.$Sentry.captureException(error);
        }
      },
      async download_as_pdf() {
        const canvas = this.$refs.qesgChart;

        const file_name = this.lang !== 'ENG' ? this.chartData.label : this.chartData_e.label;
        try {
          html2pdf(canvas, {
            margin: 1,
            filename: file_name + '.pdf',
            image: {
              type: 'jpeg',
              quality: 0.98,
            },
            html2canvas: {
              scale: 2,
            },
            jsPDF: {
              unit: 'in',
              format: 'letter',
              orientation: 'portrait',
            },
          });
        } catch (error) {
          error.message = '[차트 다운로드]' + error.message;
          this.$Sentry.captureException(error);
        }
      },
    },
  };
</script>

<style></style>
